import { type DocumentReference, serverTimestamp } from 'firebase/firestore';
import { getDownloadURL } from 'firebase/storage';

import {
    getAppStorageFileRef,
    hostingStoragePathToUrl,
} from 'services/Firebase';
import { DocumentCollectionType } from 'types/DocumentCollectionType';
import { DocumentType } from 'types/DocumentType';
import { type LogEntry } from 'types/LogEntry';
import { ProcessingStatus } from 'types/ProcessingStatus';
import { formatDateTimeLongMinutes } from 'utils/Dates';
import { slashJoin } from 'utils/String';

import { DocumentSchema } from './DocumentSchema';
import Document from './DocumentStore';
import {
    cloneDynamicPropertyDataArray,
    type DynamicPropertyData,
} from './DynamicProperties/data';
import DynamicPropertiesManager from './DynamicProperties/Manager';
import type Templates from './Templates';

export class TemplateFileProperties {
    path: string = '';
    status: ProcessingStatus = ProcessingStatus.NONE;
    storage: string = '';
}

export class TemplateSchema extends DocumentSchema {
    createTime = serverTimestamp();
    modifiedTime = serverTimestamp();
    dynamicProperties: DynamicPropertyData[] = [];
    entry: string = '';
    files: TemplateFileProperties[] = [];
    id: string = '';
    log: LogEntry[] = [];
    mutex = 0;
    name: string = '';
    prefix: string = '';
    /** @deprecated Use `status` instead. */
    processing: boolean = false;
    revision: number = NaN;
    storage: string = '';
    seedValue: unknown = {};
    status: ProcessingStatus = ProcessingStatus.NEW;
    userId: string = '';
}

export default class Template extends Document<TemplateSchema> {
    declare parent: Templates | undefined;

    schema = new TemplateSchema();
    type = DocumentType.TEMPLATE;
    collectionType = DocumentCollectionType.TEMPLATE;
    downloadURL = '';
    properties = new DynamicPropertiesManager(this, 'Template');

    constructor(parent: Templates, docRef?: DocumentReference) {
        super(parent, docRef);
        this.becomeObservable({ except: ['properties'] });
    }

    get loading() {
        return this.data.status !== ProcessingStatus.COMPLETE;
    }

    get loaded() {
        return this.data.status === ProcessingStatus.COMPLETE;
    }

    get formattedDateModified() {
        return formatDateTimeLongMinutes(this.data.modifiedTime);
    }

    saveDynamicProperties = async () => {
        this.data.dynamicProperties = this.properties.getData();
        this.properties.resetChanged();
        await this.save();
    };

    getHostedURL = () => {
        const { prefix, entry } = this.data;
        const pathname = slashJoin(prefix, entry);
        return hostingStoragePathToUrl(pathname);
    };

    onReady = async () => {
        console.log('Template ready', this.data.name);
        this.properties.setState(
            cloneDynamicPropertyDataArray(this.data.dynamicProperties)
        );
        this.properties.resetChanged();
        if (this.data.storage) {
            const templateRef = getAppStorageFileRef(this.data.storage);
            this.downloadURL = await getDownloadURL(templateRef).catch(
                (gduError) => {
                    console.warn('templateRef.getDownloadURL():', gduError);
                    return '';
                }
            );
        }
    };

    onUpdate = (data: TemplateSchema) => {
        this.properties.setState(
            cloneDynamicPropertyDataArray(data.dynamicProperties)
        );
        this.properties.resetChanged();
    };

    onReset = () => {
        this.properties = new DynamicPropertiesManager(this);
    };
}
